/**
 * ⌘K command palette — global jump menu.
 * Opens on ⌘K (Mac) / Ctrl+K (everywhere else), or by clicking the trigger button.
 *
 * v1 scope (as confirmed with user): page navigation only (8 routes).
 * Extensions (emails/opps search, recent items) can be layered on later without
 * breaking this surface.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Mail, Briefcase, TrendingUp, Activity,
  Search, Settings, ScrollText,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";

const PAGES = [
  { label: "Pipeline Overview", path: "/",              icon: LayoutGrid, keywords: "home overview kpi dashboard" },
  { label: "Emails",            path: "/emails",        icon: Mail,       keywords: "inbox mail messages" },
  { label: "Opportunities",     path: "/opportunities", icon: Briefcase,  keywords: "pipeline deals salesforce opps" },
  { label: "Business Value",    path: "/value",         icon: TrendingUp, keywords: "roi revenue value metrics executive" },
  { label: "Operations",        path: "/operations",    icon: Activity,   keywords: "sre health errors latency ops" },
  { label: "Global Search",     path: "/search",        icon: Search,     keywords: "find query lookup search" },
  { label: "Settings",          path: "/settings",      icon: Settings,   keywords: "config classifier prompt admin" },
  { label: "Audit Log",         path: "/audit",         icon: ScrollText, keywords: "history changes log audit" },
];

export function useCommandPalette() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const down = (e) => {
      // ⌘K (Mac) or Ctrl+K (Win/Linux) — globally toggles the palette,
      // including when focus is inside an input (standard power-user behaviour).
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  return { open, setOpen };
}

export function CommandPalette({ open, onOpenChange }) {
  const nav = useNavigate();

  const go = (path) => {
    onOpenChange(false);
    nav(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Jump to a page…"
        data-testid="cmdk-input"
      />
      <CommandList data-testid="cmdk-list">
        <CommandEmpty>No matches.</CommandEmpty>
        <CommandGroup heading="Navigate">
          {PAGES.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem
                key={p.path}
                value={`${p.label} ${p.keywords}`}
                onSelect={() => go(p.path)}
                data-testid={`cmdk-item-${p.path.replace(/\//g, "") || "home"}`}
              >
                <Icon size={14} className="mr-2 text-muted-foreground" />
                <span>{p.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Tips">
          <div className="px-3 py-2 text-[11px] text-muted-foreground font-mono flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="kbd">↑</span> <span className="kbd">↓</span> navigate</span>
            <span><span className="kbd">↵</span> select</span>
            <span><span className="kbd">esc</span> close</span>
            <span><span className="kbd">⌘</span> <span className="kbd">K</span> toggle</span>
          </div>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Button shown in the top-right of the sidebar that opens the palette
 * (also indicates the ⌘K shortcut visually).
 */
export function CommandPaletteTrigger({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="cmdk-trigger"
      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border text-xs text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 transition-colors bg-white"
    >
      <span className="inline-flex items-center gap-2">
        <Search size={12} /> Jump to…
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="kbd">⌘</span><span className="kbd">K</span>
      </span>
    </button>
  );
}
